/**
 * The bridge between the Interactive Console's form mode and the raw JSON payload.
 *
 * `bodyText` stays the single source of truth. The form reads `input` out of it and writes
 * `input` back — it never owns the document. That is what lets `execute()`, `copyCurl()` and
 * `buildRequestBody()` in ApiPanel.jsx stay completely unchanged.
 *
 * SPLICE, NEVER REBUILD. A form that regenerates the body from the declared fields would
 * silently delete three classes of thing that legitimately live alongside `input`:
 *
 *  1. The four LLM-App per-run overrides — system_prompt / user_prompt / output_schema /
 *     client_tools. services/workflows/llmAppSpec.js's buildRunSpec treats them as
 *     ALL-OR-NONE: 1-3 present is a 400, and all four absent on an llm_app workflow silently
 *     falls back to the saved spec — a different answer with no error, which is worse than
 *     the 400.
 *  2. A hand-typed `idempotency_key` (the Run-options field only ever ADDS one at send time).
 *  3. Any unknown key — both routers forward the whole body through as `specOverrides`, so
 *     extra keys are meaningful today and must survive a round-trip.
 */

import { valuesToInput, namedFields } from '@/components/workflow/InputSchemaForm';

/** Only scalars are representable as form controls — the input contract is string | number |
 *  boolean by design (llmAppSpec.js's INPUT_TYPES). Anything else means the author is doing
 *  something the form cannot show, so the form must stand aside rather than flatten it. */
function isScalar(v) {
  return v === null || ['string', 'number', 'boolean'].includes(typeof v);
}

/**
 * Can the form safely take over this payload?
 *
 * @returns {{ ok: true, input: object } | { ok: false, reason: string }}
 */
export function readInput(bodyText) {
  let parsed;
  try {
    parsed = JSON.parse(bodyText || '{}');
  } catch {
    return { ok: false, reason: 'The payload is not valid JSON yet.' };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { ok: false, reason: 'The payload is not a JSON object.' };
  }
  const input = parsed.input;
  if (input === undefined) return { ok: false, reason: 'The payload has no "input" object.' };
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return { ok: false, reason: '"input" is not an object.' };
  }
  const nested = Object.entries(input).find(([, v]) => !isScalar(v));
  if (nested) {
    return { ok: false, reason: `"input.${nested[0]}" holds a nested value the form cannot show.` };
  }
  return { ok: true, input };
}

/**
 * Writes `input` back into the payload, preserving every sibling key AND their order.
 *
 * Key order matters only for human diffability, but the console shows the JSON live while
 * someone types in the form — keys jumping around under them reads as corruption.
 * Re-`JSON.parse` + assign preserves insertion order for existing keys, and appends `input`
 * only when it was genuinely absent.
 */
export function writeInput(bodyText, input) {
  let parsed;
  try {
    parsed = JSON.parse(bodyText || '{}');
  } catch {
    // Unreachable through the UI (form mode is refused on unparsable JSON), but a caller
    // must never get a silently-dropped edit — start from just the input rather than throw.
    parsed = {};
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) parsed = {};
  parsed.input = input;
  return JSON.stringify(parsed, null, 2);
}

/**
 * Form values for the field list, seeded from whatever the payload already holds.
 *
 * A declared field absent from the payload stays `undefined` (the control renders empty);
 * a value present in the payload but NOT declared is left alone in the JSON — the form
 * simply does not show it, and `applyValues` below will not remove it.
 */
export function valuesFromInput(fields, input) {
  const values = {};
  for (const f of namedFields(fields)) {
    if (Object.prototype.hasOwnProperty.call(input, f.name)) values[f.name] = input[f.name];
  }
  return values;
}

/**
 * Merge edited form values back over the existing input.
 *
 * `keepEmpty: true` — the console writes on every keystroke, so a cleared field must keep its
 * key (as `""`) rather than vanish and re-appear. TestDialog's drop-empty rule is a
 * submit-time notion and stays there.
 *
 * Undeclared keys already in `input` are preserved: the schema is the form's view of the
 * payload, not a licence to prune it.
 */
export function applyValues(input, fields, values) {
  return { ...input, ...valuesToInput(fields, values, { keepEmpty: true }) };
}
